import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { useAgentAuth } from "../../contexts/AgentAuthContext";
import {
  clearCapturedAgentLogin,
  getCapturedAgentReturnPath,
  getCapturedMagicToken,
} from "../../utils/agentLoginBootstrap";
import "../../styles/agent-mobile.css";

const MagicLinkVerification = () => {
  const params = useParams();
  const tokenRef = useRef(
    getCapturedMagicToken() ||
      // The router may retain the old params after replaceState scrubs the URL.
      // Do not recover that stale token if this component later remounts.
      (/^\/agent\/verify\/[^/]+$/.test(window.location.pathname) && params.token) ||
      new URLSearchParams(window.location.search).get("token")
  );
  const returnToRef = useRef(getCapturedAgentReturnPath());
  const [status, setStatus] = useState(tokenRef.current ? "confirm" : "error");
  const submittedRef = useRef(false);
  const activeRef = useRef(false);
  const statusHeadingRef = useRef(null);
  const navigate = useNavigate();
  const { verifyMagicLink } = useAgentAuth();

  useLayoutEffect(() => {
    if (window.location.pathname !== "/agent/verify" || window.location.search) {
      window.history.replaceState({}, "", "/agent/verify");
    }
  }, []);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const confirmSignIn = async () => {
    // Lock synchronously before awaiting so repeated clicks cannot exchange the
    // one-time credential twice. Loading the page must never consume the link.
    if (submittedRef.current || !tokenRef.current) return;
    submittedRef.current = true;
    const token = tokenRef.current;
    tokenRef.current = null;
    clearCapturedAgentLogin();
    setStatus("verifying");

    try {
      const result = await verifyMagicLink(token);
      if (!activeRef.current) return;
      setStatus(result?.success ? "success" : "error");
    } catch (_error) {
      if (activeRef.current) setStatus("error");
    }
  };

  useEffect(() => {
    if (status !== "success") return undefined;
    const redirectTimer = window.setTimeout(() => {
      navigate(returnToRef.current, { replace: true });
    }, 800);
    return () => window.clearTimeout(redirectTimer);
  }, [navigate, status]);

  useEffect(() => {
    if (status !== "verifying") statusHeadingRef.current?.focus();
  }, [status]);

  return (
    <main
      id="agent-login-verification-page"
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-8"
    >
      <section id="agent-login-verification-container" className="w-full max-w-md">
        <div
          id="agent-login-verification-card"
          role="status"
          aria-live="polite"
          className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 border border-gray-100 text-center"
        >
          {status === "confirm" && (
            <div id="agent-login-confirmation-state">
              <h1
                ref={statusHeadingRef}
                tabIndex="-1"
                className="text-2xl font-bold text-gray-900 mb-2 focus:outline-none"
              >
                Confirm your sign-in
              </h1>
              <p className="text-sm text-gray-600 mb-6">
                Continue to confirm access to your email and sign in to your agent portal.
              </p>
              <button
                id="confirm-agent-sign-in"
                type="button"
                onClick={confirmSignIn}
                className="w-full min-h-11 bg-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
              >
                Confirm and sign in
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>
          )}

          {status === "verifying" && (
            <div id="agent-login-verifying-state">
              <div
                id="agent-login-verifying-icon"
                className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5"
              >
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" aria-hidden="true" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Signing you in…
              </h1>
              <p className="text-sm text-gray-600">This should only take a moment.</p>
            </div>
          )}

          {status === "success" && (
            <div id="agent-login-verification-success">
              <div
                id="agent-login-verification-success-icon"
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5"
              >
                <CheckCircle className="w-8 h-8 text-green-600" aria-hidden="true" />
              </div>
              <h1
                ref={statusHeadingRef}
                tabIndex="-1"
                className="text-2xl font-bold text-gray-900 mb-2 focus:outline-none"
              >
                You’re signed in
              </h1>
              <p className="text-sm text-gray-600 mb-6">
                Taking you back to what you were doing.
              </p>
              <button
                id="go-to-agent-dashboard"
                type="button"
                onClick={() => navigate(returnToRef.current, { replace: true })}
                className="w-full min-h-11 bg-green-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center"
              >
                Go to my dashboard
                <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>
          )}

          {status === "error" && (
            <div id="agent-login-verification-error" role="alert">
              <div
                id="agent-login-verification-error-icon"
                className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5"
              >
                <AlertCircle className="w-8 h-8 text-red-600" aria-hidden="true" />
              </div>
              <h1
                ref={statusHeadingRef}
                tabIndex="-1"
                className="text-2xl font-bold text-gray-900 mb-2 focus:outline-none"
              >
                This sign-in link is no longer available.
              </h1>
              <p className="text-sm text-gray-600 mb-6">
                Sign-in links expire after 15 minutes and can only be used once.
              </p>
              <Link
                id="request-new-agent-login-link"
                to={`/agent/login?next=${encodeURIComponent(returnToRef.current)}`}
                className="w-full min-h-11 bg-blue-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 inline-flex items-center justify-center"
              >
                Email me a new sign-in link
              </Link>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default MagicLinkVerification;
